import type { CSSProperties } from "react";
import { Icon, type IconName } from "./Icon.js";

/**
 * Portato da `components/navigation/BottomNav.jsx` del design system.
 *
 * DIFFERENZA rispetto all'originale: destinazioni e azione stanno **in una
 * pastiglia sola**, separate da un filetto. Un oggetto solo regge meglio i
 * telefoni stretti, e il filetto dice quello che prima diceva lo spazio vuoto:
 * le prime tre celle cambiano schermata, l'ultima crea qualcosa.
 */
export interface BottomNavItem<T extends string> {
  value: T;
  icon: IconName;
  label: string;
}

export interface BottomNavProps<T extends string> {
  items: BottomNavItem<T>[];
  value: T;
  onChange?: (value: T) => void;
  /** Quando presente, aggiunge il pulsante lime dell'azione principale. */
  onAdd?: () => void;
  addLabel?: string;
  style?: CSSProperties;
}

export function BottomNav<T extends string>({
  items,
  value,
  onChange,
  onAdd,
  addLabel = "Aggiungi",
  style,
}: BottomNavProps<T>) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 3,
        height: "var(--bottom-bar-height)",
        padding: "0 7px",
        borderRadius: "var(--radius-pill)",
        background: "var(--white)",
        border: "1px solid var(--border-strong)",
        ...style,
      }}
    >
        {items.map((item) => {
          const active = item.value === value;

          return (
            <button
              key={item.value}
              type="button"
              onClick={() => onChange?.(item.value)}
              aria-label={item.label}
              aria-current={active ? "page" : undefined}
              style={{
                width: 46,
                height: 46,
                border: "none",
                cursor: "pointer",
                borderRadius: "var(--radius-pill)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: active ? "var(--ink-900)" : "transparent",
                color: active ? "var(--white)" : "var(--ink-800)",
                transition:
                  "background var(--duration-fast) var(--ease-standard), color var(--duration-fast) var(--ease-standard)",
              }}
            >
              <Icon name={item.icon} size={21} />
            </button>
          );
        })}
      {onAdd && (
        <>
          {/*
            Il filetto è l'unica cosa che distingue l'azione dalle destinazioni,
            ora che condividono la stessa pastiglia. Senza, la quarta cella si
            legge come una quarta schermata.
          */}
          <span
            aria-hidden="true"
            style={{
              width: 1,
              height: 26,
              flexShrink: 0,
              margin: "0 4px",
              background: "var(--border-strong)",
            }}
          />

          <button
            type="button"
            onClick={onAdd}
            aria-label={addLabel}
            style={{
              width: 46,
              height: 46,
              minWidth: 46,
              border: "none",
              cursor: "pointer",
              borderRadius: "var(--radius-pill)",
              background: "var(--lime-400)",
              color: "var(--text-on-lime)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon name="plus" size={22} strokeWidth={2.2} />
          </button>
        </>
      )}
    </div>
  );
}
